import { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { connectBank, getBankConnections, syncBankTransactions, disconnectBank } from '../services/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Bank, ArrowsClockwise, Plug, Trash, Check, Warning, CurrencyDollar } from '@phosphor-icons/react';

export default function BankFeedsPage() {
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [syncing, setSyncing] = useState(null);

  useEffect(() => { fetchConnections(); }, []);

  const fetchConnections = async () => {
    setLoading(true);
    try {
      const res = await getBankConnections();
      setConnections(res.data.connections);
    } catch (e) { toast.error('Failed to load connections'); }
    finally { setLoading(false); }
  };

  const handleConnect = async () => {
    setConnecting(true);
    try {
      await connectBank();
      toast.success('Bank connected (Demo Mode)');
      fetchConnections();
    } catch (e) { toast.error('Connection failed'); }
    finally { setConnecting(false); }
  };

  const handleSync = async (connId) => {
    setSyncing(connId);
    try {
      const res = await syncBankTransactions(connId);
      toast.success(res.data.message);
    } catch (e) { toast.error('Sync failed'); }
    finally { setSyncing(null); }
  };

  const handleDisconnect = async (connId) => {
    if (!window.confirm('Disconnect this bank?')) return;
    try {
      await disconnectBank(connId);
      toast.success('Disconnected');
      fetchConnections();
    } catch (e) { toast.error('Failed'); }
  };

  if (loading) {
    return (<Layout title="Bank Feeds"><div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-zinc-950 border-t-transparent rounded-full animate-spin"></div></div></Layout>);
  }

  return (
    <Layout title="Bank Feeds">
      <div data-testid="bank-feeds-page" className="space-y-6">
        {/* Info Banner */}
        <div className="bg-amber-50 border border-amber-200 rounded-sm p-4 flex items-start gap-3">
          <Warning size={20} className="text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-amber-900 font-medium">Demo Mode</p>
            <p className="text-xs text-amber-700">Bank feeds are running in demo mode with sample data. Connect your Plaid API keys to enable real bank connections.</p>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <p className="text-sm text-zinc-600">Connect bank accounts to automatically import transactions.</p>
          <Button onClick={handleConnect} disabled={connecting} className="bg-zinc-950 text-white rounded-sm" data-testid="connect-bank-button">
            <Plug size={16} className="mr-2" /> {connecting ? 'Connecting...' : 'Connect Bank'}
          </Button>
        </div>

        {connections.length === 0 ? (
          <div className="bg-white border border-zinc-200 rounded-sm p-12 text-center">
            <Bank size={48} className="mx-auto mb-4 text-zinc-300" />
            <p className="font-medium mb-1">No banks connected</p>
            <p className="text-sm text-zinc-500">Connect a bank to start importing transactions automatically.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {connections.map((conn) => (
              <div key={conn.connection_id} className="bg-white border border-zinc-200 rounded-sm" data-testid={`bank-conn-${conn.connection_id}`}>
                <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 border border-blue-200 rounded-sm flex items-center justify-center">
                      <Bank size={20} weight="duotone" className="text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold">{conn.institution}</h3>
                      <p className="text-xs text-zinc-500">
                        {conn.is_mock && <span className="text-amber-600 mr-2">DEMO</span>}
                        Connected {new Date(conn.created_at).toLocaleDateString()}
                        {conn.last_synced && ` | Last synced: ${new Date(conn.last_synced).toLocaleString()}`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" onClick={() => handleSync(conn.connection_id)} disabled={syncing === conn.connection_id} className="rounded-sm" data-testid={`sync-${conn.connection_id}`}>
                      <ArrowsClockwise size={16} className={`mr-2 ${syncing === conn.connection_id ? 'animate-spin' : ''}`} />
                      {syncing === conn.connection_id ? 'Syncing...' : 'Sync'}
                    </Button>
                    <Button variant="outline" onClick={() => handleDisconnect(conn.connection_id)} className="rounded-sm border-red-200 text-red-600 hover:bg-red-50">
                      <Trash size={16} />
                    </Button>
                  </div>
                </div>

                {/* Accounts */}
                <div className="px-6 py-4">
                  <p className="text-xs uppercase tracking-widest text-zinc-500 mb-3">Linked Accounts</p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {(conn.accounts || []).map((acct) => (
                      <div key={acct.account_id} className="p-4 bg-zinc-50 border border-zinc-100 rounded-sm">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium">{acct.name}</span>
                          <CurrencyDollar size={14} className="text-zinc-400" />
                        </div>
                        <p className="text-lg font-mono font-bold">
                          <span className={acct.balance >= 0 ? 'text-emerald-600' : 'text-red-600'}>
                            ${Math.abs(acct.balance).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                          </span>
                        </p>
                        <p className="text-xs text-zinc-500 capitalize">{acct.subtype} | {acct.currency}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
